/**
 * Video Call Reminder System
 * 
 * ตรวจสอบและส่ง reminder notifications สำหรับ scheduled video calls
 */

import { collection, query, where, getDocs, doc, updateDoc, addDoc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { notificationModel } from '../data/notificationModel';

/**
 * ตรวจสอบ scheduled video calls ที่ใกล้ถึงเวลา
 * และส่ง reminder notifications
 */
export const checkAndSendReminders = async () => {
  try {
    const now = new Date();
    const fifteenMinutesFromNow = new Date(now.getTime() + 15 * 60 * 1000);
    const oneMinuteFromNow = new Date(now.getTime() + 1 * 60 * 1000);

    // Query scheduled requests ที่:
    // 1. status = 'approved' หรือ 'scheduled'
    // 2. scheduledTime อยู่ในช่วง 15 นาที (สำหรับ reminder)
    // 3. scheduledTime อยู่ในช่วง 1 นาที (สำหรับ starting now)
    const requestsRef = collection(db, 'videoCallRequests');
    
    // Get all approved/scheduled requests (filter by time in code to avoid composite index)
    const approvedQuery = query(
      requestsRef,
      where('status', 'in', ['approved', 'scheduled'])
    );

    const allSnapshot = await getDocs(approvedQuery);
    const allRequests = allSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter by time in code
    const reminderRequests = allRequests.filter(request => {
      if (!request.scheduledTime) return false;
      const scheduledTime = request.scheduledTime?.toDate ? request.scheduledTime.toDate() : new Date(request.scheduledTime);
      const timeUntilCall = scheduledTime.getTime() - now.getTime();
      return timeUntilCall >= 0 && timeUntilCall <= 15 * 60 * 1000; // Within 15 minutes
    });

    const startingRequests = allRequests.filter(request => {
      if (!request.scheduledTime) return false;
      const scheduledTime = request.scheduledTime?.toDate ? request.scheduledTime.toDate() : new Date(request.scheduledTime);
      const timeUntilCall = scheduledTime.getTime() - now.getTime();
      return timeUntilCall >= 0 && timeUntilCall <= 1 * 60 * 1000; // Within 1 minute
    });

    // Send 15-minute reminders
    for (const request of reminderRequests) {
      // Check if reminder already sent
      if (request.reminderSent) continue;

      const scheduledTime = request.scheduledTime?.toDate ? request.scheduledTime.toDate() : new Date(request.scheduledTime);
      const timeUntilCall = scheduledTime.getTime() - now.getTime();
      const minutesUntilCall = Math.floor(timeUntilCall / (60 * 1000));

      // Only send if exactly 15 minutes before (or close to it)
      if (minutesUntilCall >= 14 && minutesUntilCall <= 16) {
        await sendReminderNotification(request, '15min');
      }
    }

    // Send "starting now" notifications
    for (const request of startingRequests) {
      // Check if notification already sent
      if (request.startingNotificationSent) continue;

      await sendReminderNotification(request, 'starting');
    }

    return {
      remindersSent: reminderRequests.length,
      startingNotificationsSent: startingRequests.length
    };
  } catch (error) {
    console.error('Error checking and sending reminders:', error);
    throw error;
  }
};

/**
 * ส่ง reminder notification
 */
const sendReminderNotification = async (request, type) => {
  try {
    const scheduledTime = request.scheduledTime?.toDate ? request.scheduledTime.toDate() : new Date(request.scheduledTime);
    const formattedTime = scheduledTime.toLocaleString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    const notificationsRef = collection(db, notificationModel.collection);

    if (type === '15min') {
      // Send to buyer
      const buyerNotification = notificationModel.createNotification(
        request.buyerId,
        notificationModel.types.VIDEO_CALL_REMINDER,
        '⏰ Video Call จะเริ่มใน 15 นาที',
        `Video Call สำหรับ "${request.productName || 'N/A'}" จะเริ่มใน 15 นาที (${formattedTime})`,
        {
          requestId: request.id,
          storeId: request.storeId,
          productId: request.productId,
          scheduledTime: request.scheduledTime,
          type: 'reminder_15min'
        }
      );

      await addDoc(notificationsRef, {
        ...buyerNotification,
        read: false,
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send to store owner
      const sellerNotification = notificationModel.createNotification(
        request.sellerId,
        notificationModel.types.VIDEO_CALL_REMINDER,
        '⏰ Video Call จะเริ่มใน 15 นาที',
        `Video Call กับ "${request.buyerName || 'ผู้ซื้อ'}" จะเริ่มใน 15 นาที (${formattedTime})`,
        {
          requestId: request.id,
          storeId: request.storeId,
          productId: request.productId,
          scheduledTime: request.scheduledTime,
          type: 'reminder_15min'
        }
      );

      await addDoc(notificationsRef, {
        ...sellerNotification,
        read: false,
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Mark reminder as sent
      const requestRef = doc(db, 'videoCallRequests', request.id);
      await updateDoc(requestRef, {
        reminderSent: true,
        reminderSentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

    } else if (type === 'starting') {
      // Send to buyer
      const buyerNotification = notificationModel.createNotification(
        request.buyerId,
        notificationModel.types.VIDEO_CALL_REMINDER,
        '📹 Video Call เริ่มแล้ว!',
        `Video Call สำหรับ "${request.productName || 'N/A'}" เริ่มแล้ว! คลิกเพื่อเข้าร่วม`,
        {
          requestId: request.id,
          storeId: request.storeId,
          productId: request.productId,
          scheduledTime: request.scheduledTime,
          type: 'starting_now',
          action: 'join_call'
        }
      );

      await addDoc(notificationsRef, {
        ...buyerNotification,
        read: false,
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Send to store owner
      const sellerNotification = notificationModel.createNotification(
        request.sellerId,
        notificationModel.types.VIDEO_CALL_REMINDER,
        '📹 Video Call เริ่มแล้ว!',
        `Video Call กับ "${request.buyerName || 'ผู้ซื้อ'}" เริ่มแล้ว! คลิกเพื่อเข้าร่วม`,
        {
          requestId: request.id,
          storeId: request.storeId,
          productId: request.productId,
          scheduledTime: request.scheduledTime,
          type: 'starting_now',
          action: 'join_call'
        }
      );

      await addDoc(notificationsRef, {
        ...sellerNotification,
        read: false,
        readAt: null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Mark notification as sent
      const requestRef = doc(db, 'videoCallRequests', request.id);
      await updateDoc(requestRef, {
        startingNotificationSent: true,
        startingNotificationSentAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    console.log(`✅ Sent ${type} reminder for request:`, request.id);
  } catch (error) {
    console.error('Error sending reminder notification:', error);
    throw error;
  }
};

/**
 * Start reminder checking service
 * เรียกใช้ทุก 1 นาที
 */
export const startReminderService = (intervalMinutes = 1) => {
  // Check immediately
  checkAndSendReminders().catch(console.error);

  // Then check every interval
  const intervalMs = intervalMinutes * 60 * 1000;
  const intervalId = setInterval(() => {
    checkAndSendReminders().catch(console.error);
  }, intervalMs);

  return intervalId;
};

